import type { SliderProps } from "@mui/material";
import { FormControl, FormHelperText, Slider, styled } from "@mui/material";
import { useField, useFormikContext } from "formik";
import { useCallback } from "react";

import { transientOptions } from "../../utilities";
import { useFormContext } from "../FormContext";
import { useFieldContext } from "../FormFieldContext";
import { Label } from "../Labels";
import { FieldText } from "./FieldText";
import { useFieldLabelAndDescription } from "./helpers";

type Props = SliderProps & {
  direction?: "row" | "column";
  subtleStyling?: boolean;
  includeInput?: boolean;
};

export const FieldSlider = ({ direction, subtleStyling, includeInput, ...sliderProps }: Props) => {
  const { submitOnBlur } = useFormContext();
  const { submitForm } = useFormikContext();

  const { controlId, name } = useFieldContext();
  const [{ value }, , { setValue }] = useField<number | number[]>(name);
  const [label, helperText] = useFieldLabelAndDescription(name);

  const handleChange = useCallback(
    (_event: Event, newValue: number | number[], _activeThumb: number) => {
      void setValue(newValue);
    },
    [setValue],
  );

  const handleChangeCommitted = () => {
    if (submitOnBlur) {
      void submitForm();
    }
  };

  return (
    <StyledFormControl $direction={direction}>
      {label && <Label>{label}</Label>}
      {includeInput && <FieldText label="" style={{ width: "40px" }} />}
      <StyledSlider
        {...sliderProps}
        $subtleStyling={subtleStyling}
        id={controlId}
        name={name}
        value={value}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </StyledFormControl>
  );
};

const StyledSlider = styled(Slider, transientOptions)<{ $subtleStyling?: boolean }>`
  ${({ $subtleStyling }) =>
    $subtleStyling &&
    `
      opacity: 0.5;
      transition: opacity 0.2s ease-in-out;
      &:hover,
      &:active,
      &:focus {
        opacity: 1;
      }
  `};
`;

const StyledFormControl = styled(FormControl, transientOptions)<{ $direction?: "row" | "column" }>`
  width: 100%;
  ${({ $direction }) =>
    $direction === "row" &&
    `
      flex-direction: row;
      align-items: center;
      gap: 15px;
  `};
`;
